import { roleLabel } from "AppConstants";
import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Close as CloseIcon, InfoIcon as InfoOutlinedIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { checkUserHasAdminRole } from "app/services/app.state.service";
import { parseApiError } from "app/services/api.error.parser";
import { addUserToBackend, updateUserToBackend } from "app/services/user.service";
import MDBox from "components/MDBox";
import { validateUserObject } from "utils/userValidationUtil";
import { emailFieldProps, normalizeEmail } from "utils/emailValidationUtil";
// Shared with UsersDataGrid so the dialog and the row actions cannot disagree on which roles are
// privileged (HRMS-263).
import { isPrivilegedRole } from "utils/userRolePolicy";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  id: null,
  fullName: "",
  email: "",
  password: "",
  status: "ACTIVE",
  type: "EMPLOYEE",
  role: "MEMBER",
  // @since NEXT_VERSION
  employeeCode: "",
  departmentId: "",
};

const roles = ["ADMIN", "MASTER_DATA_ADMIN", "MEMBER", "MANAGER", "ACCOUNT", "BDM"];


const passwordPolicyTooltip = (
  <MDBox sx={{ p: 0.5, textAlign: "left" }}>
    <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 1.5 }}>
      Password must contain:
    </Typography>
    <Typography variant="caption" component="div" sx={{ textAlign: "left" }}>
      • 8 or more characters
    </Typography>
    <Typography variant="caption" component="div" sx={{ textAlign: "left" }}>
      • At least one uppercase letter (A-Z)
    </Typography>
    <Typography variant="caption" component="div" sx={{ textAlign: "left" }}>
      • At least one lowercase letter (a-z)
    </Typography>
    <Typography variant="caption" component="div" sx={{ textAlign: "left" }}>
      • At least one digit (0-9)
    </Typography>
    <Typography variant="caption" component="div" sx={{ textAlign: "left" }}>
      • At least one special character (@ # $ % ^ &amp; + =)
    </Typography>
    <Typography variant="caption" component="div" sx={{ textAlign: "left" }}>
      • No whitespace
    </Typography>
  </MDBox>
);

const FieldLabel = ({ children, required, tooltip }) => (
  <MDBox display="flex" alignItems="center" sx={{ mb: 0.75 }}>
    <Typography sx={{ fontSize:'14px', fontWeight: 600, color: "#6c757d" }}>
      {children}
      {required && <span style={{ color: "#d32f2f", marginLeft: 2 }}>*</span>}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right" arrow>
        <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }} aria-label="field info">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </MDBox>
);

const AddUserDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const departments = useSelector((state) => state.department.rows);

  useEffect(() => {
    setData(obj);
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
  }, [open]);

  useEffect(() => {
    if (editObject) {
      setData((prevData) => ({
        ...prevData,
        id: parseInt(editObject.id),
        fullName: editObject.fullName,
        email: editObject.email,
        password: editObject.password,
        status: editObject.status,
        type: editObject.type,
        role: editObject.roles[0].role.replace("EMPLOYEE_", ""),
        // @since NEXT_VERSION
        employeeCode: editObject.employeeCode ?? "",
        departmentId: editObject.departmentId ?? "",
      }));
    }
  }, [open, editObject]);

  // Options are the ACTIVE departments plus, when editing, the user's current department even if it
  // has since been disabled. Without the union an unmatched MUI Select value renders blank and the
  // assignment is silently wiped on save.
  const departmentOptions = (() => {
    const active = (departments ?? []).filter((d) => d.status === "ACTIVE");
    const currentId = data?.departmentId;
    if (!currentId) return active;
    if (active.some((d) => String(d.id) === String(currentId))) return active;

    const current = (departments ?? []).find((d) => String(d.id) === String(currentId));
    const fallbackLabel = editObject?.departmentName ?? `Department ${currentId}`;
    return [
      ...active,
      {
        id: currentId,
        code: current?.code ?? "",
        displayName: `${current?.displayName ?? fallbackLabel} (inactive)`,
        status: "DISABLED",
      },
    ];
  })();

  // Only an Admin may hand out the privileged roles, or touch a user who already holds one. For
  // everyone else the current value is still the sole option so the Select does not render blank and
  // silently rewrite the role on save, the same reason the department options are unioned above.
  // @since NEXT_VERSION
  const isAdmin = checkUserHasAdminRole();
  const isRoleLocked = !isAdmin && Boolean(editObject) && isPrivilegedRole(data?.role);
  const roleOptions = (() => {
    if (isRoleLocked) return [data.role];
    const options = isAdmin ? roles : roles.filter((role) => !isPrivilegedRole(role));
    // A role the list above does not offer must still show for a user who already holds it, or the
    // Select renders blank and rewrites the role on save. Added while BDM was hidden; kept now that
    // it is back, because the same trap waits for any role that is ever withdrawn from the list.
    return !data?.role || options.includes(data.role) ? options : [...options, data.role];
  })();

  const saveUserInBackend = async () => {
    const isClient = data.type === "CLIENT";
    // trimTextFields leaves the password alone, so the credential is never altered.
    const payload = trimTextFields({
      ...data,
      email: normalizeEmail(data.email),
      // @since NEXT_VERSION — mirrors UserAdapter: a client user carries neither field.
      employeeCode: isClient ? null : (data.employeeCode || "").trim().toUpperCase() || null,
      departmentId: isClient || !data.departmentId ? null : parseInt(data.departmentId, 10),
    });
    if (validateUserObject(payload) && !editObject) await addUserInvoker(payload);
    else if (validateUserObject(payload) && editObject) await updateUserInvoker(payload);
    else {
      setErrorAction("One or more required fields for a User are missing or having invalid value");
    }
  };

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const addUserInvoker = async (userObject) => {
    await addUserToBackend(userObject)
      .then((data) => {
        setSuccessAction("User added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to add user. Please try again.");
      });
  };

  const updateUserInvoker = async (data) => {
    const editObjectId = data.id;
    let editData = data;
    delete editData.roles;
    if(editData.type === "CLIENT")
      editData = {...editData, role: null};
    await updateUserToBackend(editObjectId, editData)
      .then((data) => {
        setSuccessAction("User Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to update user. Please try again.");
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update User` : `Add New User`}</DialogTitle>
      <DialogContent>
        {/* <DialogContentText></DialogContentText> */}
        <MDBox my={2}>
          <TextField
            id="full_name"
            label="Full Name"
            fullWidth
            variant="outlined"
            value={data?.fullName || ""}
            onBlur={trimOnBlur(setData, "fullName")}
            onChange={(e) => setData({ ...data, fullName: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="email"
            label="Email"
            fullWidth
            variant="outlined"
            value={data?.email || ""}
            onBlur={trimOnBlur(setData, "email")}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            {...emailFieldProps(data?.email)}
            slotProps={{ htmlInput: { autoComplete: "off" } }}
          />
        </MDBox>
        {!data.id && (
          <MDBox my={2}>
            <FieldLabel tooltip={passwordPolicyTooltip}>Password</FieldLabel>
            <TextField
              id="password"
              type="password"
              fullWidth
              variant="outlined"
              placeholder="Enter password"
              onChange={(e) => setData({ ...data, password: e.target.value })}
              slotProps={{ htmlInput: { autoComplete: "new-password" } }}
            />
          </MDBox>
        )}
        <MDBox my={2}>
          <TextField
            select
            id="type"
            label="Type"
            fullWidth
            variant="outlined"
            value={data?.type || "EMPLOYEE"}
            onChange={(e) => {setData({ ...data, type: e.target.value })}}
            helperText="*Required"
          >
            <MenuItem key="EMPLOYEE" value="EMPLOYEE">
              EMPLOYEE
            </MenuItem>
            <MenuItem key="CLIENT" value="CLIENT">
              CLIENT
            </MenuItem>
          </TextField>
        </MDBox>
        {data.type === "EMPLOYEE" && (
          <>
            <MDBox my={2}>
              <TextField
                select
                id="role"
                label="Role"
                fullWidth
                variant="outlined"
                value={data?.role || "MEMBER"}
                onChange={(e) => setData({ ...data, role: e.target.value })}
                disabled={isRoleLocked}
                helperText={isRoleLocked ? "Only an Admin can change this role" : "*Required"}
              >
                {roleOptions.map((role) => (
                  // Value is the stored role; the label is what HRMS-116 asks to be shown.
                  <MenuItem key={role} value={role}>
                    {roleLabel(role)}
                  </MenuItem>
                ))}
              </TextField>
            </MDBox>
            {/* @since NEXT_VERSION */}
            <MDBox my={2}>
              <TextField
                id="employee_code"
                label="Employee Code"
                fullWidth
                variant="outlined"
                value={data?.employeeCode || ""}
                onBlur={trimOnBlur(setData, "employeeCode")}
                onChange={(e) =>
                  setData({ ...data, employeeCode: e.target.value.toUpperCase() })
                }
                helperText="*Required for employees (e.g. EMP-0042)"
              />
            </MDBox>
            {/* @since NEXT_VERSION */}
            <MDBox my={2}>
              <TextField
                select
                id="department"
                label="Department"
                fullWidth
                variant="outlined"
                value={data?.departmentId ?? ""}
                onChange={(e) => setData({ ...data, departmentId: e.target.value })}
                helperText="*Required for employees"
              >
                {departmentOptions.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.displayName}
                  </MenuItem>
                ))}
              </TextField>
            </MDBox>
          </>
        )}
        <MDBox my={2}>
          <TextField
            id="status"
            select
            label="Active"
            fullWidth
            variant="outlined"
            value={data?.status || "ACTIVE"}
            onChange={(e) => setData({ ...data, status: e.target.value })}
            helperText="*Required"
          >
            <MenuItem key="ACTIVE" value="ACTIVE">
              YES
            </MenuItem>
            <MenuItem key="DISABLED" value="DISABLED">
              No
            </MenuItem>
          </TextField>
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveUserInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddUserDialog);